import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Scale, Activity, FileText, Star, ShieldCheck, Truck, MessageCircle, Zap } from 'lucide-react';
import { db } from '../firebase/firebase-config';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import './ProductDetail.css';
import { useLanguage } from '../../context/LanguageContext';

const ProductDetail = () => {
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const product = location.state?.product;
  
  const [activeTab, setActiveTab] = useState('specs');
  const [heroImage, setHeroImage] = useState('');
  const { lang } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchHero = async () => {
      const sliderSnap = await getDocs(query(collection(db, "sliders"), orderBy("createdAt", "desc"), limit(1)));
      if (!sliderSnap.empty) setHeroImage(sliderSnap.docs[0].data().url);
    };
    fetchHero();
  }, []);

  // KANKA: Dinamik Firebase verilerini 3 dile göre (TR, EN, AR) çeviren merkezi fonksiyon
  const getAutoTranslate = (text, type = 'description') => {
    // Kanka Firestore'da product.nameAr veya product.descriptionAr varsa direkt onları kullanıyoruz
    if (lang === 'TR') return text;

    if (type === 'description') {
      if (lang === 'AR') {
        return product?.descriptionAr || "محلول تغذية متميز عالي الأداء مصمم خصيصاً لصحة الماشية المثالية وأقصى إنتاجية. تم إنتاجه بمعايير بيغما.";
      }
      // İngilizce mantığı kanka
      return product?.descriptionEn || "High-performance premium feed solution specifically formulated for optimal livestock health and maximum yield."; 
    }

    if (type === 'name') {
      if (lang === 'AR') return product?.nameAr || product?.name;
      if (lang === 'EN') return product?.nameEn || product?.name;
      return text;
    }

    return text;
  };

  const handleWhatsApp = () => {
    const whatsappNumber = "905XXXXXXXXX"; // Kanka buraya gerçek numarayı bağla
    const productName = getAutoTranslate(product?.name, 'name');
    
    const message = lang === 'TR' 
      ? `Merhaba, ${productName} ürününüz hakkında bilgi almak istiyorum.`
      : lang === 'AR'
      ? `مرحباً، أود الحصول على معلومات حول منتجكم ${productName}.`
      : `Hello, I would like to get information about your product ${productName}.`;

    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (!product) return <div className="premium-loader">BIGMA</div>;

  return (
    <div className="product-page-wrapper">
      <Navbar />

      <section className="detail-hero" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="hero-overlay-dark">
          <div className="container">
             <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="hero-text-area">
                <button className="back-btn-premium" onClick={() => navigate(-1)}>
                  <ChevronLeft size={18} /> 
                  <span>{lang === 'TR' ? "Üretim Gruplarına Dön" : lang === 'AR' ? "العودة إلى مجموعات الإنتاج" : "Back to Production Groups"}</span>
                </button>

                <div className="hero-status-badges">
                  <span className="badge-item"><Star size={14} fill="#f59e0b" color="#f59e0b"/> {lang === 'TR' ? "Popüler Seçim" : lang === 'AR' ? "خيار شائع" : "Popular Choice"}</span>
                  <span className="badge-item"><ShieldCheck size={14} /> {lang === 'TR' ? "Laboratuvar Onaylı" : lang === 'AR' ? "معتمد مخبرياً" : "Lab Certified"}</span>
                </div>
                
                <h1 className="product-title-large">
                  {getAutoTranslate(product.name, 'name')}
                </h1>
                <p className="hero-brief">
                  {product.subType?.toUpperCase()} {lang === 'TR' ? "GRUBU PREMIUM BESLEME ÇÖZÜMÜ" : lang === 'AR' ? "حلول تغذية متميزة للمجموعة" : "GROUP PREMIUM FEEDING SOLUTION"}
                </p>
             </motion.div>
          </div>
        </div>
      </section>

      <div className="detail-tabs-bar">
        <div className="container">
          <div className="tabs-flex">
            {[
              { id: 'specs', label: lang === 'TR' ? 'Analiz Değerleri' : lang === 'AR' ? 'قيم التحليل' : 'Analysis Values', icon: <Activity size={18}/> },
              { id: 'description', label: lang === 'TR' ? 'Ürün Özellikleri' : lang === 'AR' ? 'مميزات المنتج' : 'Product Features', icon: <FileText size={18}/> },
              { id: 'supply', label: lang === 'TR' ? 'Tedarik ve Destek' : lang === 'AR' ? 'التوريد والدعم' : 'Supply & Support', icon: <Truck size={18}/> }
            ].map((tab) => (
              <button key={tab.id} className={activeTab === tab.id ? 'active' : ''} onClick={() => setActiveTab(tab.id)}>
                {tab.icon} <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="product-main-content">
        <div className="container">
          <div className="content-layout-grid">
            <div className="content-visual-side sticky-box">
              <div className="visual-card-premium">
                <img src={product.imageUrl} alt={product.name} className="main-product-img" />
                <div className="product-quick-info">
                  <div className="q-info"><Scale size={16}/> {product.weight} {lang === 'AR' ? "كجم" : "KG"}</div>
                  <div className="q-info">
                    <Zap size={16}/> 
                    {lang === 'TR' ? (product.type || "Toz/Pelet") : 
                     lang === 'AR' ? (product.typeAr || "مسحوق/مكبوس") : 
                     (product.typeEn || "Powder/Pellet")}
                  </div>
                </div>
                
                {/* WhatsApp İletişim Butonu kanka */}
                <button className="whatsapp-order-btn" onClick={handleWhatsApp}>
                  <MessageCircle size={20} />
                  <span>{lang === 'TR' ? "Bilgi Al & Sipariş" : lang === 'AR' ? "استفسار وطلب" : "Info & Order"}</span>
                </button>
              </div>
            </div>

            <div className="content-text-side">
              <AnimatePresence mode="wait">
                <motion.div key={activeTab} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
                  {activeTab === 'description' && (
                    <div className="tab-panel">
                      <h2 className="content-heading">{lang === 'TR' ? "Ürün Hakkında Detaylar" : lang === 'AR' ? "تفاصيل حول المنتج" : "Details About the Product"}</h2>
                      <div className="premium-desc-box">
                        <p className="description-text-p">
                          {getAutoTranslate(product.description, 'description')}
                        </p>
                      </div>
                    </div>
                  )}

                  {activeTab === 'specs' && (
                    <div className="tab-panel">
                       <h2 className="content-heading">{lang === 'TR' ? "Analiz Parametreleri" : lang === 'AR' ? "معايير التحليل" : "Analysis Parameters"}</h2>
                       {/* Kanka analiz değerleri tablosunu da dile duyarlı şekilde buraya basabilirsin */}
                    </div>
                  )}

                  {activeTab === 'supply' && (
                    <div className="tab-panel">
                       <h2 className="content-heading">{lang === 'TR' ? "Lojistik ve Teknik Destek" : lang === 'AR' ? "الدعم اللوجستي والفني" : "Logistics & Technical Support"}</h2>
                       <p className="description-text-p">
                         {lang === 'TR' 
                           ? "Ürünlerimiz 25 kg ve 50 kg'lık paketler halinde sunulmaktadır. Sivas fabrikamızdan tüm bölgelere gönderim mevcuttur." 
                           : lang === 'AR'
                           ? "منتجاتنا متوفرة في عبوات 25 كجم و 50 كجم. الشحن متاح لجميع المناطق من مصنعنا في سيواس."
                           : "Our products are offered in 25 kg and 50 kg packages. Shipping is available to all regions from our Sivas factory."}
                       </p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetail;